<script setup>
import GoogleAdsSearchTermInsightActionsCell from "@frontJs/components/tables/cells/projectManagement/seo/GoogleAdsSearchTermInsightActionsCell.vue";
import Container from "@js/components/Container.vue";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import NumberCell from "@js/components/tables/cells/NumberCell.vue";
import PriceCellV2 from "@js/components/tables/cells/PriceCellV2.vue";
import StatusCellV2 from "@js/components/tables/cells/StatusCellV2.vue";
import TextCellV2 from "@js/components/tables/cells/TextCellV2.vue";
import { useDataTable } from "@js/utils/dataTable";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";

const { tenant, targetedLanguages, websiteVariants, targetableGoogleWebsites } =
  defineProps({
    tenant: {
      type: Object,
      required: true,
    },
    targetedLanguages: {
      type: Array,
      required: true,
    },
    websiteVariants: {
      type: Array,
      required: true,
    },
    targetableGoogleWebsites: {
      type: Array,
      required: true,
    },
  });

const { t } = useI18n();

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  setGlobalFilter,
  availableFilters,
  filters,
  setFilters,
  updateData,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "segmentsSearchTerm",
      header: () => t("label.keyword"),
      cell: (props) =>
        h(TextCellV2, {
          text: props.row.original.segmentsSearchTerm,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "countOfSeoTrackedKeywords",
      header: () => t("label.keywordAdded"),
      cell: (props) =>
        h(StatusCellV2, {
          status: props.row.original.countOfSeoTrackedKeywords > 0,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "seoTrackedKeywordImportantToFollow",
      header: () => t("label.importantToFollow"),
      cell: (props) =>
        props.row.original.countOfSeoTrackedKeywords > 0 &&
        props.row.original.seoTrackedKeyword.importantToFollow
          ? h(StatusCellV2, {
              status: 1,
            })
          : undefined,
    }),
    columnHelper.accessor(() => null, {
      id: "sumOfMetricsClicks",
      header: () => t("label.clicks"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.sumOfMetricsClicks,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "sumOfMetricsImpressions",
      header: () => t("label.impressions"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.sumOfMetricsImpressions,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "sumOfMetricsConversions",
      header: () => t("label.conversions"),
      cell: (props) =>
        h(NumberCell, {
          number: props.row.original.sumOfMetricsConversions,
          withDecimals: true,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "sumOfMetricsConversionsValue",
      header: () => t("label.conversionsValue"),
      cell: (props) =>
        h(PriceCellV2, {
          price: props.row.original.sumOfMetricsConversionsValue,
        }),
    }),
    columnHelper.display({
      id: "actions",
      header: t("label.actions"),
      cell: (innerProps) =>
        h(GoogleAdsSearchTermInsightActionsCell, {
          tenant: tenant,
          row: innerProps.row,
          targetableGoogleWebsites: targetableGoogleWebsites,
          websiteVariants: websiteVariants,
          targetedLanguages: targetedLanguages,
          callback: updateData,
        }),
      width: 0,
    }),
  ],
  url: route("front.projectManagement.seo.postGoogleAdsSearchTermInsights", {
    tenant: tenant.id,
  }),
  columnPinning: {
    right: ["actions"],
  },
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <template v-else>
    <Container class="wysiwyg mb-4">
      <ul>
        <li>Mots-clés relevés par Google Ads sur les 12 derniers mois</li>
      </ul>
    </Container>

    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :set-global-filter="setGlobalFilter"
      :available-filters="availableFilters"
      :filters="filters"
      :set-filters="setFilters"
    />
  </template>
</template>
